import React from 'react';
import Icon from '../../../components/AppIcon';

// ---------------------------------------------------------------------------
// SignalBreakdownCard — the caseload sliced by signal ("who needs a look").
//
// analyzeCaseload() labels each student with one of five signals:
//   critical  — safety concern OR compliance overdue
//   attention — trend concern (stalled/regressing goal, review due soon,
//               behavior spike)
//   strong    — trending up with no red flags
//   steady    — some data, no signal either way
//   quiet     — no data yet
//
// This card shows the five buckets as a horizontal stacked bar (like a
// mini status bar) plus a small legend, so the teacher gets a one-glance
// answer to "how many of my students need me this week."
// ---------------------------------------------------------------------------

const SIGNALS = [
  {
    key: 'critical',
    label: 'Critical',
    description: 'Safety concern or IEP compliance overdue.',
    color: 'bg-red-500',
    dot: 'bg-red-500',
    text: 'text-red-700',
  },
  {
    key: 'attention',
    label: 'Attention',
    description: 'Trend needs a look this week.',
    color: 'bg-amber-500',
    dot: 'bg-amber-500',
    text: 'text-amber-700',
  },
  {
    key: 'strong',
    label: 'Strong',
    description: 'Trending up, no red flags.',
    color: 'bg-emerald-500',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
  },
  {
    key: 'steady',
    label: 'Steady',
    description: 'Data coming in, no signal either way.',
    color: 'bg-slate-400',
    dot: 'bg-slate-400',
    text: 'text-slate-600',
  },
  {
    key: 'quiet',
    label: 'Quiet',
    description: 'Not enough data to say yet.',
    color: 'bg-slate-200',
    dot: 'bg-slate-300',
    text: 'text-slate-500',
  },
];

const SignalBreakdownCard = ({ summary }) => {
  if (!summary) return null;
  const counts = summary.studentsBySignal || {};
  const total = SIGNALS.reduce((n, s) => n + (counts[s.key] || 0), 0) || 1;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Activity" size={18} className="text-slate-500" />
        <h3 className="font-semibold text-slate-900">Where your caseload stands</h3>
      </div>

      <div className="flex h-3 rounded-full overflow-hidden mb-4 bg-slate-100">
        {SIGNALS.map((s) => {
          const n = counts[s.key] || 0;
          if (n === 0) return null;
          const pct = (n / total) * 100;
          return (
            <div
              key={s.key}
              className={s.color}
              style={{ width: `${pct}%` }}
              title={`${s.label}: ${n}`}
            />
          );
        })}
      </div>

      <ul className="space-y-2">
        {SIGNALS.map((s) => {
          const n = counts[s.key] || 0;
          return (
            <li key={s.key} className="flex items-start gap-3">
              <span className={`w-2.5 h-2.5 rounded-full mt-1.5 ${s.dot}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className={`font-medium text-sm ${s.text}`}>{s.label}</span>
                  <span className="text-sm font-semibold text-slate-900">{n}</span>
                </div>
                <p className="text-xs text-slate-500">{s.description}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SignalBreakdownCard;
